import { Component, signal, computed, WritableSignal } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { BlankValueComponent, CustomCellComponent } from '@vendasta/galaxy/table';
import { FieldValueInterface } from '@vendasta/crm';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { SystemFieldIds } from '../../../shared-services';
import { UserWithDisplayName } from '../../../tokens-and-interfaces';
import { ActivityAssigneeService } from '../../../shared-services';

const NO_VALUE_FOR_USER = '<NO_VALUE_FOR_USER>';

@Component({
  template: `
    <ng-container *ngIf="user() as user; else loading">
      <div *ngIf="user !== NO_VALUE_FOR_USER; else blank">
        <span>{{ userName() }}</span>
      </div>
    </ng-container>
    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  imports: [CommonModule, BlankValueComponent],
})
export class UserCellComponent extends CustomCellComponent {
  NO_VALUE_FOR_USER = NO_VALUE_FOR_USER;
  private fieldValues = signal<FieldValueInterface[]>([]);

  set value(newValues: FieldValueInterface[]) {
    this.fieldValues.set(newValues);
  }

  user: WritableSignal<UserWithDisplayName | '<NO_VALUE_FOR_USER>' | null> = signal(null);
  userName = computed(() => {
    const user = this.user();
    if (user === NO_VALUE_FOR_USER) {
      return '';
    }
    return user?.displayName ?? '';
  });

  constructor(private readonly userService: ActivityAssigneeService) {
    super();
    toObservable(this.fieldValues)
      .pipe(
        switchMap((fieldValues) => {
          const userId =
            fieldValues.find((fieldValue) => fieldValue?.fieldId === SystemFieldIds.ActivityOwnerID) ?? null;
          if (!userId || !userId?.stringValue) {
            return of(NO_VALUE_FOR_USER);
          }
          return this.userService.getMultiUsers([userId.stringValue]);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        if (result === NO_VALUE_FOR_USER || !result.length) {
          this.user.set(NO_VALUE_FOR_USER);
          return;
        }
        const user = result[0] as UserWithDisplayName;
        if (!user?.userId) {
          this.user.set(NO_VALUE_FOR_USER);
          return;
        }
        this.user.set(user);
      });
  }
}
